/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {LookupService} from '../../common/service/lookup.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {FeatureApplyDeleteService} from './feature-apply-delete.service';
import {InterestService} from './features/interest.service';
import {RoadService} from './features/road.service';
import {InterestReconciliationService} from '../../common/service/interest-reconciliation.service';
import {SpatialUtilService} from './spatial-util.service';
import {WidgetService} from './widget.service';
import {FeatureAssociationService} from './features/feature-association.service';
import {SpatialSnapshotService} from '../snapshot/spatial-snapshot.service';
import {LineTypeLookup} from '../../common/models/line-type.model';
import {of, tap} from 'rxjs';
import {NgEventBus} from 'ng-event-bus';
import {first, switchMap} from 'rxjs/operators';

interface FeatureMap {
    affectedLot?: any[];
    angle?: any[];
    annotation?: any[];
    controlPoint?: any[];
    point?: any[];
    line?: any[];
    polygon?: any[];
    lot?: any[];
    road?: any[];
    constructPoint?: any[];
    constructLine?: any[];
    interest?: any[];
    polyline?: any[];
}

@Injectable({
    providedIn: 'root'
})
export class FeaturesDeletionService {
    title = 'FeaturesDeletionService';

    allAnnotations = [];

    allAngles = [];

    featuresToBeDeleted: FeatureMap = {
        affectedLot: [],
        angle: [],
        annotation: [],
        controlPoint: [],
        point: [],
        line: [],
        polygon: [],
        lot: [],
        road: [],
        constructPoint: [],
        constructLine: [],
        interest: [],
        polyline: []
    };

    featuresToBeUpdated: FeatureMap = {
        affectedLot: [],
        angle: [],
        annotation: [],
        controlPoint: [],
        point: [],
        line: [],
        polygon: [],
        lot: [],
        road: [],
        constructPoint: [],
        constructLine: [],
        interest: []
    };

    interestCache = {};

    constructor(
        private eventBus: NgEventBus,
        private featuresApplyDeleteService: FeatureApplyDeleteService,
        private featureAssociationService: FeatureAssociationService,
        private interestService: InterestService,
        private interestReconciliationService: InterestReconciliationService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private roadService: RoadService,
        private spatialSnapshotService: SpatialSnapshotService,
        private spatialUtilService: SpatialUtilService,
        private widgetService: WidgetService
    ) {}

    deleteFeatures(features?: any[], featureLayer?: any, options?: {createSnapshot: boolean}) {
        console.debug(`${this.title} - deleteFeatures`);

        const selectionSet = this.spatialUtilService.getHighlightedFeatureSets(features && featureLayer && [featureLayer]);

        //ND-1096 - prohibiting to delete annotation of Enlargement - only to be deleted along with Enlargement
        selectionSet
            .filter((v: any) => v.featureLayer.id === 'annotationFeatureLayer')
            .forEach((v: any) =>
                v.graphics.splice(0, v.graphics.length, ...v.graphics.filter((g: any) => g.attributes.featuretype !== 'enlargement'))
            );

        this.allAngles = this.spatialUtilService.getAngles();

        const allAnnotations = this.spatialUtilService.getAnnotations();
        this.allAnnotations = allAnnotations;

        return (options?.createSnapshot === false ? of(null) : this.spatialSnapshotService.createSnapshot({showAlert: true})).pipe(
            switchMap(() => {
                if (features && featureLayer) {
                    const featureLayerType = this.spatialUtilService.getFeatureLayerName(featureLayer);

                    this.addFeaturesToBeDeleted(features, featureLayerType);

                    if (featureLayerType === 'annotation') {
                        this.addAnnotationFeaturesToBeRemoved(features, allAnnotations);
                    } else {
                        this.deleteAnnotations(features, featureLayerType);
                    }

                    this.deleteAssociatedFeatures(features, featureLayer);

                    return this.deleteFeaturesInternal().pipe(
                        tap(() => {
                            const featureIds = features.map(f => f.attributes.id);
                            const graphicsToUnhighlight = selectionSet[0].graphics.filter((g: any) => featureIds.includes(g.attributes.id));
                            this.spatialUtilService.unhighlightFeatures(featureLayer, graphicsToUnhighlight);
                        })
                    );
                } else {
                    selectionSet.forEach((selection: any) => {
                        if (selection.graphics.length > 0) {
                            const featureClass = this.spatialUtilService.getFeatureClass(selection.featureLayer);
                            const featureLayerType = this.spatialUtilService.getFeatureLayerName(selection.featureLayer);

                            // Do not remove business features as they should be removed through attribute viewer
                            if (!['affectedLot', 'interest', 'lot', 'road'].includes(featureLayerType)) {
                                this.addFeaturesToBeDeleted(selection.graphics, featureClass === 'controlpoint' ? 'controlPoint' : featureLayerType);

                                if (featureLayerType === 'annotation') {
                                    const group = [];

                                    selection.graphics.forEach((feature: any) => {
                                        this.findGroupAnnotations(allAnnotations, group, feature);
                                        group.push(feature);
                                    });

                                    this.addAnnotationFeaturesToBeRemoved(group, allAnnotations);
                                } else {
                                    this.deleteAnnotations(selection.graphics, featureLayerType);
                                }

                                this.spatialUtilService.unhighlightFeatures(selection.featureLayer, selection.graphics);
                            }
                        }
                    });

                    let allFeaturesDeleted = true;
                    this.featuresToBeDeleted = this.getReconciledFeatureMap(this.featuresToBeDeleted);

                    for (const [featureType, features] of Object.entries(this.featuresToBeDeleted) as any[]) {
                        if (this.spatialUtilService.getFeatures(featureType).length !== features.length) {
                            allFeaturesDeleted = false;
                            break;
                        }
                    }

                    if (!allFeaturesDeleted) {
                        selectionSet
                            .filter(
                                (selection: any) =>
                                    selection.graphics.length &&
                                    !['affectedLot', 'interest', 'lot', 'road'].includes(
                                        this.spatialUtilService.getFeatureClass(selection.featureLayer)
                                    )
                            )
                            .forEach((selection: any) => this.deleteAssociatedFeatures(selection.graphics, selection.featureLayer));
                    }

                    return this.deleteFeaturesInternal();
                }
            }),
            first()
        );
    }

    private parseLinePointIds(linePointIdsString: string) {
        if (!linePointIdsString) {
            return [];
        }

        return linePointIdsString.split(',').map(s => ({
            type: (s.match(/\[(\d+)]/) && 'point') || (s.match(/{(\d+)}/) && 'controlPoint') || (s.match(/\((\d+)\)/) && 'constructPoint'),
            id: Number.parseInt(s.replace(/[[\]{}()]/, ''))
        }));
    }

    private parseLinePointIdsToPointTypeIdsMap(linePointIdsString: string) {
        return this.parseLinePointIds(linePointIdsString).reduce(
            (acc, v) => {
                acc[v.type].push(v.id);
                return acc;
            },
            {point: [], controlPoint: [], constructPoint: []}
        );
    }

    private toAssociatedPointTypeIdsMap(featureMap: FeatureMap, ignoreDeleteOrphanedPoints: boolean) {
        return [featureMap.line, featureMap.constructLine].reduce(
            (acc, features) => {
                features.forEach(f => {
                    const lineTypeLookup = this.getLineTypeLookups().find(l => l.id === f.attributes.type);
                    if (ignoreDeleteOrphanedPoints || (lineTypeLookup && lineTypeLookup.deleteOrphanedPoints)) {
                        if (f.attributes.frompointid && !acc.point.includes(f.attributes.frompointid)) {
                            acc.point.push(f.attributes.frompointid);
                        }

                        if (f.attributes.topointid && !acc.point.includes(f.attributes.topointid)) {
                            acc.point.push(f.attributes.topointid);
                        }

                        const pointTypeIdsMap = this.parseLinePointIdsToPointTypeIdsMap(f.attributes.pointids);

                        acc.point.push(...pointTypeIdsMap.point.filter(p => !acc.point.includes(p)));
                        acc.constructPoint.push(...pointTypeIdsMap.constructPoint.filter(p => !acc.constructPoint.includes(p)));
                        acc.controlPoint.push(...pointTypeIdsMap.controlPoint.filter(p => !acc.controlPoint.includes(p)));
                    }
                });

                return acc;
            },
            {
                point: [],
                constructPoint: [],
                controlPoint: []
            }
        );
    }

    private getLineTypeLookups(): LineTypeLookup[] {
        return this.lookupService.getLookups('lineType');
    }

    private addOrphanedPointsToBeDeleted(featuresToBeDeleted: FeatureMap) {
        const pointTypeIdsMapForFeaturesToDelete = this.toAssociatedPointTypeIdsMap(featuresToBeDeleted, false);

        const pointTypeIdsMapForFeaturesToRetain = this.toAssociatedPointTypeIdsMap(
            ['line', 'constructLine'].reduce(
                (acc, k) => {
                    const idsOfFeaturesToDelete = featuresToBeDeleted[k].map((f: any) => f.attributes.id);
                    acc[k] = this.spatialUtilService.getGraphics(k).filter((g: any) => !idsOfFeaturesToDelete.includes(g.attributes.id));
                    return acc;
                },
                {line: [], constructLine: []}
            ),
            true
        );

        const typeIdsMapOfPointsToDelete = Object.keys(pointTypeIdsMapForFeaturesToDelete).reduce((acc, k) => {
            acc[k] = pointTypeIdsMapForFeaturesToDelete[k].filter((id: number) => !pointTypeIdsMapForFeaturesToRetain[k].includes(id));
            return acc;
        }, {});

        Object.keys(typeIdsMapOfPointsToDelete).forEach(k => {
            const ids = typeIdsMapOfPointsToDelete[k].filter((id: number) => !featuresToBeDeleted[k].map((p: any) => p.attributes.id).includes(id));
            const features = this.spatialUtilService.getGraphics(k).filter((g: any) => ids.includes(g.attributes.id));
            featuresToBeDeleted[k].push(...features);
        });
    }

    private getCachedInterest(id: number | string) {
        if (!this.interestCache[id]) {
            const interest = this.spatialUtilService.findById('interest', id);
            if (interest) {
                this.interestCache[id] = this.spatialUtilService.copyGraphic(interest);
            }
        }
        return this.interestCache[id];
    }

    private getCachedInterests() {
        return this.spatialUtilService.getInterests().map((i: any) => this.getCachedInterest(i.attributes.id));
    }

    private clearCachedInterest(id: number | string) {
        if (this.interestCache[id]) {
            delete this.interestCache[id];
        }
    }

    private findRelatedAnnotations(features: any[], parentId: any) {
        return features.filter((feature: any) => feature.attributes.parentid === parentId);
    }

    private findGroupAnnotations(features: any[], annotations: any[], feature: any) {
        const {attribute9, attribute10, featuretype: featureType, featureid: featureId} = feature.attributes;
        if (!attribute10) {
            return;
        }

        annotations.push(
            ...features.filter(f => {
                const attrs = f.attributes;
                return (
                    attrs.attribute10 &&
                    attrs.attribute10 === attribute10 &&
                    (attrs.attribute9 ? attrs.attribute9 === attribute9 : attrs.featuretype === featureType && attrs.featureid === featureId) &&
                    !annotations.includes(f)
                );
            })
        );
    }

    private addAnnotationFeaturesToBeRemoved(features: any[], annotations: any[]) {
        const allAnnotationsToBeRemoved = [];

        features.forEach((feature: any) => {
            annotations.forEach((annotation: any) => {
                if (feature.attributes.id === annotation.attributes.parentid) {
                    allAnnotationsToBeRemoved.push(annotation, ...this.findRelatedAnnotations(annotations, annotation.attributes.parentid));
                }

                if (feature.attributes.parentid === annotation.attributes.id) {
                    allAnnotationsToBeRemoved.push(annotation, ...this.findRelatedAnnotations(annotations, annotation.attributes.id));
                }

                this.findGroupAnnotations(annotations, allAnnotationsToBeRemoved, feature);
            });
        });

        this.addFeaturesToBeDeleted(allAnnotationsToBeRemoved, 'annotation');
    }

    /***
     * Delete associated features in the specified feature layer or layer type.
     * @param features Features for which associated features must be deleted.
     * @param featureLayer Feature layer of features.
     * @returns {*}
     */
    private deleteAssociatedFeatures(features: any[], featureLayer: any) {
        const featureLayerType = this.spatialUtilService.getFeatureLayerName(featureLayer);

        console.debug(`${this.title} - deleteAssociatedFeatures - featureLayerType: ${featureLayerType}`);

        const associatedFeatures: {
            angles: any[];
            constructPoints: any[];
            constructLines: any[];
            points: any[];
            lines: any[];
            polygons: any[];
        } = this.findAssociatedFeaturesToBeRemoved(features, featureLayer);

        // DCX-7523: remove annotation related points:
        if (featureLayerType === 'annotation' && associatedFeatures.points.length > 0) {
            this.deleteAssociatedFeatures(associatedFeatures.points, this.spatialUtilService.getPointMapLayer());
        }

        // Delete hidden angles
        if (featureLayerType === 'point') {
            this.deleteAssociatedAngles(features, associatedFeatures.lines);
        } else if (featureLayerType === 'line') {
            this.deleteAssociatedAngles([], features);
        }

        if (associatedFeatures.polygons.length > 0) {
            this.addFeaturesToBeDeleted(associatedFeatures.polygons, 'polygon');
            this.deleteAnnotations(associatedFeatures.polygons, 'polygon');
        }

        if (associatedFeatures.lines.length > 0) {
            this.addFeaturesToBeDeleted(associatedFeatures.lines, 'line');
            this.deleteAnnotations(associatedFeatures.lines, 'line');
        }

        if (associatedFeatures.constructLines.length > 0) {
            this.addFeaturesToBeDeleted(associatedFeatures.constructLines, 'constructLine');
            this.deleteAnnotations(associatedFeatures.constructLines, 'constructLine');
        }

        if (associatedFeatures.constructPoints.length > 0) {
            this.addFeaturesToBeDeleted(associatedFeatures.constructPoints, 'constructPoint');
            this.deleteAnnotations(associatedFeatures.constructPoints, 'constructPoint');
        }

        if (associatedFeatures.angles.length > 0) {
            this.addFeaturesToBeDeleted(associatedFeatures.angles, 'angle');
        }

        window.setTimeout(() => {
            if (this.widgetService.isWidgetActive('IdentifyFeatures')) {
                this.eventBus.cast('resetFeaturesInteracted');
            }
        }, 200);
    }

    private findAssociatedFeaturesToBeRemoved(features: any[], featureLayer: any) {
        const featureLayerType = this.spatialUtilService.getFeatureLayerName(featureLayer);

        console.debug(`${this.title} - findAssociatedFeaturesToBeRemoved - featureLayerType: ${featureLayerType}`);

        const output = {
            affectedLots: [],
            angles: [],
            points: [],
            lines: [],
            polygons: [],
            lots: [],
            interests: [],
            constructPoints: [],
            constructLines: []
        };

        features.forEach(feature => {
            const affectedLots = [],
                angles = [],
                points = [],
                lines = [],
                polygons = [],
                lots = [],
                interests = [],
                constructLines = [],
                constructPoints = [];

            if (featureLayerType === 'annotation') {
                this.getAssociatedFeaturesByAnnotationToBeDeleted(feature, 'line', lines);
                this.getAssociatedFeaturesByAnnotationToBeDeleted(feature, 'constructLine', constructLines);
                this.getAssociatedFeaturesByAnnotationToBeDeleted(feature, 'constructPoint', constructPoints);

                this.getAssociatedFeaturesByAnnotationToBeDeleted(feature, 'point', points);
                points.forEach(p => this.getAssociatedLinesByPointToBeDeleted('point', p, lines));

                this.getAssociatedFeaturesByAnnotationToBeDeleted(feature, 'controlPoint', points);
                points.forEach(p => this.getAssociatedLinesByPointToBeDeleted('controlPoint', p, lines));

                this.getAssociatedTotalLotBoundaryLinesToBeDeleted(lines);
                this.getAssociatedConstructPointsToBeDeleted(constructLines, constructPoints);
                this.getAssociatedConstructLinesToBeDeleted(constructPoints, constructLines);
                this.getAssociatedConstructPointsToBeDeleted(constructLines, constructPoints);
                this.getAssociatedAnglesByLinesToBeDeleted(lines, angles);
                this.getAssociatedPolygonsByLinesToBeDeleted(lines, polygons);
                this.getAssociatedLotsByPolygonsToBeDeleted(polygons, lots);
                this.getAssociatedInterestsByPolygonsToBeDeleted(polygons, interests);
            } else if (featureLayerType === 'point' || featureLayerType === 'controlPoint') {
                this.getAssociatedLinesByPointToBeDeleted(featureLayerType, feature, lines);
                this.getAssociatedTotalLotBoundaryLinesToBeDeleted(lines);
                this.getAssociatedConstructLinesToBeDeleted([feature], constructLines);
                this.getAssociatedPolylinesToBeDeleted(feature, lines, featureLayerType);
                this.getAssociatedConstructPointsToBeDeleted(constructLines, constructPoints);
                this.getAssociatedConstructLinesToBeDeleted(constructPoints, constructLines);

                this.getAssociatedAnglesByPointToBeDeleted(feature, angles);
                this.getAssociatedAnglesByLinesToBeDeleted(lines, angles);

                this.getAssociatedPolygonsByLinesToBeDeleted(lines, polygons);
                this.getAssociatedLotsByPolygonsToBeDeleted(polygons, lots);
                this.getAssociatedInterestsByPolygonsToBeDeleted(polygons, interests);
            } else if (featureLayerType === 'constructPoint') {
                this.getAssociatedConstructLinesToBeDeleted([feature], constructLines);
                this.getAssociatedConstructPointsToBeDeleted(constructLines, constructPoints);
                this.getAssociatedPolylinesToBeDeleted(feature, lines, featureLayerType);
            } else if (featureLayerType === 'constructLine') {
                this.getAssociatedConstructPointsToBeDeleted([feature], constructPoints);
                this.getAssociatedConstructLinesToBeDeleted(constructPoints, constructLines);
                this.getAssociatedConstructPointsToBeDeleted(constructLines, constructPoints);
            } else if (featureLayerType === 'line') {
                lines[feature.attributes.id] = feature;
                this.getAssociatedAnglesByLinesToBeDeleted(lines, angles);
                this.getAssociatedPolygonsByLinesToBeDeleted(lines, polygons);
                this.getAssociatedTotalLotBoundaryLinesToBeDeleted(lines);
                polygons.length && this.getAssociatedLotsByPolygonsToBeDeleted(polygons, lots);
                polygons.length && this.getAssociatedInterestsByPolygonsToBeDeleted(polygons, interests);
            } else if (featureLayerType === 'polygon') {
                polygons[feature.attributes.id] = feature;
                this.getAssociatedLotsByPolygonsToBeDeleted(polygons, lots);
                this.getAssociatedInterestsByPolygonsToBeDeleted(polygons, interests);
            }

            output.affectedLots.push(...affectedLots.filter(v => v));
            output.angles.push(...angles.filter(v => v));
            output.points.push(...points.filter(v => v));
            output.lines.push(...lines.filter(v => v));
            output.polygons.push(...polygons.filter(v => v));
            output.lots.push(...lots.filter(v => v));
            output.constructPoints.push(...constructPoints.filter(v => v));
            output.constructLines.push(...constructLines.filter(v => v));
            output.interests.push(...interests.filter(v => v));
        });

        return output;
    }

    private deleteAssociatedAngles(points: any[], lines: any[]) {
        console.debug(`${this.title} - deleteAssociatedAngles`);

        this.allAngles.forEach((angle: any) => {
            // find associated hidden angles by points
            points.forEach((point: any) => {
                if (point.attributes.id === angle.attributes.pointid) {
                    this.addFeaturesToBeDeleted([angle], 'angle');
                }
            });

            // find associated hidden angles by lines
            lines.forEach((line: any) => {
                if (line.attributes.id === angle.attributes.anglefromlineid || line.attributes.id === angle.attributes.angletolineid) {
                    this.addFeaturesToBeDeleted([angle], 'angle');
                }
            });
        });
    }

    private deleteFullyDisassociatedInterestAnnotations(featureClass: string, features: any[]) {
        if (!['line', 'polygon'].includes(featureClass)) {
            return;
        }

        const featureIds = features.map(f => f.attributes.id);

        const interestsToBeFullyDisassociated =
            featureClass === 'polygon'
                ? this.getCachedInterests().filter(
                      (i: any) =>
                          !i.attributes.polylineids &&
                          i.attributes.polygonids &&
                          this.spatialUtilService.parseIdsAttr(i.attributes.polygonids).every(id => featureIds.includes(id))
                  )
                : this.getCachedInterests().filter(
                      (i: any) =>
                          !i.attributes.polygonids &&
                          i.attributes.polylineids &&
                          this.spatialUtilService.parseIdsAttr(i.attributes.polylineids).every(id => featureIds.includes(id))
                  );

        if (interestsToBeFullyDisassociated.length) {
            const interestsToBeFullyDisassociatedIds = interestsToBeFullyDisassociated.map((i: any) => i.attributes.id);
            const interestAnnotationsToDelete = this.allAnnotations.filter(
                (a: any) => a.attributes.featuretype === 'interest' && interestsToBeFullyDisassociatedIds.includes(a.attributes.featureid)
            );
            if (interestAnnotationsToDelete.length) {
                this.addFeaturesToBeDeleted(interestAnnotationsToDelete, 'annotation');
            }
        }

        this.getCachedInterests().forEach((i: any) => {
            if (featureClass === 'polygon') {
                if (i.attributes.polygonids) {
                    i.attributes.polygonids = this.spatialUtilService.toIdsAttr(
                        this.spatialUtilService.parseIdsAttr(i.attributes.polygonids).filter(id => !featureIds.includes(id))
                    );
                }
            } else {
                if (i.attributes.polylineids) {
                    i.attributes.polylineids = this.spatialUtilService.toIdsAttr(
                        this.spatialUtilService.parseIdsAttr(i.attributes.polylineids).filter(id => !featureIds.includes(id))
                    );
                }
            }
        });
    }

    private deleteFullyDisassociatedRoadAnnotations(featureClass: string, features: any[]) {
        if (featureClass !== 'polygon') {
            return;
        }

        const polygonIds = features.map(f => f.attributes.id);

        const fullyDisassociatedRoadIds = this.spatialUtilService
            .getRoads()
            .filter(
                (r: any) =>
                    r.attributes.polygonids && this.spatialUtilService.parseIdsAttr(r.attributes.polygonids).every(id => polygonIds.includes(id))
            )
            .map((r: any) => r.attributes.id);

        if (fullyDisassociatedRoadIds.length) {
            const roadAnnotationsToDelete = this.allAnnotations.filter(
                (a: any) => a.attributes.featuretype === 'road' && fullyDisassociatedRoadIds.includes(a.attributes.featureid)
            );
            if (roadAnnotationsToDelete.length) {
                this.addFeaturesToBeDeleted(roadAnnotationsToDelete, 'annotation');
            }
        }
    }

    private deleteAnnotations(features: any[], type: string) {
        console.debug(`${this.title} - deleteAnnotations`);

        this.deleteFullyDisassociatedInterestAnnotations(type, features);

        this.deleteFullyDisassociatedRoadAnnotations(type, features);

        this.allAnnotations.forEach((annotation: any) => {
            features.forEach((feature: any) => {
                let canDelete = false;

                if (annotation.attributes.featureid === feature.attributes.id && annotation.attributes.featuretype === type) {
                    canDelete = true;
                } else if (annotation.attributes.childtype === type && String(annotation.attributes.childid) === String(feature.attributes.id)) {
                    canDelete = true;
                } else if (
                    type === 'line' &&
                    feature.attributes.pointids &&
                    annotation.attributes.childtype === 'polyline' &&
                    String(annotation.attributes.childid) === String(feature.attributes.id)
                ) {
                    canDelete = true;
                } else if (
                    type === 'road' &&
                    annotation.attributes.featuretype === 'road' &&
                    annotation.attributes.featuresubtype === 10 &&
                    annotation.attributes.featureid === feature.attributes.id
                ) {
                    canDelete = true;
                }

                if (canDelete) {
                    this.addFeaturesToBeDeleted([annotation], 'annotation');
                }
            });
        });
    }

    private getAssociatedFeaturesByAnnotationToBeDeleted(annotation: any, associatedFeatureType: string, associatedFeatures: any) {
        console.debug(`${this.title} - getAssociatedFeaturesByAnnotationToBeDeleted`);

        if (associatedFeatureType === 'controlPoint') {
            const controlPointFeatures = this.spatialUtilService.getControlPoints().filter((g: any) => {
                return (
                    g.attributes.id === annotation.attributes.featureid &&
                    annotation.attributes.featuretype === 'point' &&
                    !associatedFeatures[g.attributes.id]
                );
            });
            controlPointFeatures.forEach((feature: any) => (associatedFeatures[feature.attributes.id] = feature));
            this.addFeaturesToBeDeleted(controlPointFeatures, associatedFeatureType);
        } else {
            const features = this.spatialUtilService
                .getFeatures(associatedFeatureType)
                .filter(
                    (associatedFeature: any) =>
                        !associatedFeatures[associatedFeature.attributes.id] &&
                        annotation.attributes.featuretype === associatedFeatureType &&
                        annotation.attributes.featureid === associatedFeature.attributes.id
                );
            features.forEach((feature: any) => (associatedFeatures[feature.attributes.id] = feature));
            this.addFeaturesToBeDeleted(features, associatedFeatureType);
        }
    }

    private getAssociatedLinesByPointToBeDeleted(featureType: string, feature: any, associatedLines: any) {
        console.debug(`${this.title} - getAssociatedLinesByPointToBeDeleted`);

        const lines = this.spatialUtilService
            .getLines()
            .filter(
                (line: any) =>
                    !associatedLines[line.attributes.id] &&
                    featureType === 'point' &&
                    (feature.attributes.id === line.attributes.frompointid || feature.attributes.id === line.attributes.topointid)
            );

        lines.forEach((line: any) => (associatedLines[line.attributes.id] = line));

        this.addFeaturesToBeDeleted(lines, 'line');
    }

    private getAssociatedTotalLotBoundaryLinesToBeDeleted(associatedLines: any[]) {
        console.debug(`${this.title} - getAssociatedTotalLotBoundaryLinesToBeDeleted`);

        const totalLotBoundaryIds = associatedLines.map(l => l.attributes.totallotboundaryid).filter(id => id);

        const lines = this.spatialUtilService
            .getLines()
            .filter((line: any) => line.attributes.lineids && totalLotBoundaryIds.some(id => line.attributes.id === id));

        lines.forEach((line: any) => (associatedLines[line.attributes.id] = line));
        this.addFeaturesToBeDeleted(lines, 'line');
    }

    private getAssociatedConstructLinesToBeDeleted(points: any[], associatedConstructLines: any) {
        console.debug(`${this.title} - getAssociatedConstructLinesToBeDeleted`);

        const constructLines = this.spatialUtilService
            .getConstructLines()
            .filter(
                (constructLine: any) =>
                    !associatedConstructLines[constructLine.attributes.id] &&
                    points.some(
                        point =>
                            (point.attributes.sourcelineids && point.attributes.sourcelineids.includes(`[${constructLine.attributes.id}]`)) ||
                            `[${point.attributes.id}]` === constructLine.attributes.frompointid ||
                            `[${point.attributes.id}]` === constructLine.attributes.topointid
                    )
            );
        constructLines.forEach((constructLine: any) => (associatedConstructLines[constructLine.attributes.id] = constructLine));
        this.addFeaturesToBeDeleted(constructLines, 'constructLine');
    }

    private getAssociatedPolylinesToBeDeleted(point: any, associatedLines: any, featureLayerType: string) {
        console.debug(`${this.title} - getAssociatedPolylinesToBeDeleted`);

        const lines = this.spatialUtilService.getLines().filter((line: any) => {
            if (line.attributes.pointids) {
                switch (featureLayerType) {
                    case 'controlPoint':
                    case 'point':
                        return line.attributes.pointids.includes(`[${point.attributes.id}]`);
                    case 'constructPoint':
                        return line.attributes.pointids.includes(`(${point.attributes.id})`);
                }
            }
        });

        lines.forEach((line: any) => (associatedLines[line.attributes.id] = line));
        this.addFeaturesToBeDeleted(lines, 'line');
    }

    private getAssociatedConstructPointsToBeDeleted(constructLines: any[], associatedConstructPoints: any) {
        console.debug(`${this.title} - getAssociatedPointsToBeDeleted`);

        const constructLineIds = constructLines.reduce((acc, constructLine) => {
            if (!acc.includes(constructLine.attributes.id)) {
                acc.push(constructLine.attributes.id);
            }
            return acc;
        }, []);

        if (constructLineIds.length === 0) {
            return;
        }

        const constructPoints = this.spatialUtilService.getConstructPoints().filter((constructPoint: any) => {
            return (
                constructPoint.attributes.sourcelineids &&
                constructLineIds.some((constructLineId: any) => constructPoint.attributes.sourcelineids.includes(`[${constructLineId}]`))
            );
        });

        constructPoints.forEach((constructPoint: any) => (associatedConstructPoints[constructPoint.attributes.id] = constructPoint));
        this.addFeaturesToBeDeleted(constructPoints, 'constructPoint');
    }

    private getAssociatedAnglesByPointToBeDeleted(point: any, associatedAngles: any) {
        console.debug(`${this.title} - getAssociatedAnglesByPointToBeDeleted`);

        this.allAngles.forEach((angle: any) => {
            if (point.attributes.id === angle.attributes.pointid && associatedAngles[angle.attributes.id] === undefined) {
                associatedAngles[angle.attributes.id] = angle;
                this.addFeaturesToBeDeleted([angle], 'angle');
            }
        });
    }

    private getAssociatedAnglesByLinesToBeDeleted(lines: any[], associatedAngles: any) {
        console.debug(`${this.title} - getAssociatedAnglesByLinesToBeDeleted`);

        this.allAngles.forEach((angle: any) => {
            lines.forEach((line: any) => {
                if (
                    line.attributes.id === angle.attributes.anglefromlineid ||
                    (line.attributes.id === angle.attributes.angletolineid && associatedAngles[angle.attributes.id] === undefined)
                ) {
                    associatedAngles[angle.attributes.id] = angle;
                    this.addFeaturesToBeDeleted([angle], 'angle');
                }
            });
        });
    }

    private getAssociatedPolygonsByLinesToBeDeleted(lines: any[], associatedPolygons: any) {
        console.debug(`${this.title} - getAssociatedPolygonsByLinesToBeDeleted`);

        if (lines.length === 0) {
            return;
        }

        const polygons = this.spatialUtilService
            .getPolygons()
            .filter(
                (polygon: any) =>
                    !associatedPolygons[polygon.attributes.id] &&
                    polygon.attributes.lineids &&
                    lines.some(line => polygon.attributes.lineids.includes(`[${line.attributes.id}]`))
            );

        polygons.forEach((polygon: any) => (associatedPolygons[polygon.attributes.id] = polygon));
        this.addFeaturesToBeDeleted(polygons, 'polygon');
    }

    private getAssociatedLotsByPolygonsToBeDeleted(polygons: any[], associatedLots: any) {
        console.debug(`${this.title} - getAssociatedLotsByPolygonsToBeDeleted`);

        if (polygons.length === 0) {
            return;
        }

        const lots = this.spatialUtilService
            .getLots()
            .filter(
                (lot: any) =>
                    !associatedLots[lot.attributes.id] &&
                    lot.attributes.polygonids &&
                    polygons.some(polygon => lot.attributes.polygonids.includes(`[${polygon.attributes.id}]`))
            );

        lots.forEach((lot: any) => (associatedLots[lot.attributes.id] = lot));
        //NOTE: Do not remove business features as they should be removed by using the attribute viewer
    }

    private getAssociatedInterestsByPolygonsToBeDeleted(polygons: any[], associatedInterests: any) {
        console.debug(`${this.title} - getAssociatedInterestsByPolygonsToBeDeleted`);

        if (polygons.length === 0) {
            return;
        }

        const interests = this.spatialUtilService
            .getInterests()
            .filter(
                (interest: any) =>
                    !associatedInterests[interest.attributes.id] &&
                    interest.attributes.polygonids &&
                    polygons.some(polygon => interest.attributes.polygonids.includes(`[${polygon.attributes.id}]`))
            );

        interests.forEach((interest: any) => (associatedInterests[interest.attributes.id] = interest));
        //NOTE: Do not remove business features as they should be removed by using the attribute viewer
    }

    private addFeaturesToBeDeleted(features: any[], type: string) {
        console.debug(`${this.title} - addFeaturesToBeDeleted`);

        const featuresToBeDeleted = this.featuresToBeDeleted[type];
        if (featuresToBeDeleted) {
            featuresToBeDeleted.push(...features.filter(f => !featuresToBeDeleted.includes(f)));
        }
    }

    private getReconciledFeatures(features: any[], type: string) {
        console.debug(`${this.title} - getReconciledFeatures`);

        return features.filter(feature => this.featuresToBeDeleted[type].some((f: any) => f.attributes.id === feature.attributes.id));
    }

    private deleteFeaturesInternal() {
        console.debug(`${this.title} - deleteFeaturesInternal`);

        const featuresToBeDeleted = this.cloneFeatureMap(this.featuresToBeDeleted);

        this.featuresToBeDeleted = this.getCleanFeatureMap();

        const reconciledMainObj = this.getReconciledFeatureMap(featuresToBeDeleted);

        this.addOrphanedPointsToBeDeleted(reconciledMainObj);

        const entriesToDelete = Object.entries(reconciledMainObj).filter(kv => kv[1].length);
        if (entriesToDelete.length === 0) {
            return of(null);
        }

        let observable = of(null);

        entriesToDelete.forEach(kv => {
            observable = observable.pipe(
                switchMap(() => {
                    const type = kv[0];
                    const features = kv[1];
                    const featureLayer = this.spatialUtilService.getFeatureLayer(type);

                    return this.featuresApplyDeleteService.commitFeaturesDeletion(featureLayer, features, true).pipe(
                        tap(() => {
                            console.debug(`${this.title} - deleteFeaturesInternal - ${type} - success`);

                            // Remove parent association on polygons
                            if (features[0].attributes.polygonids) {
                                this.unAssociateParentFromAssociatedPolygons(features, type);
                            }

                            // Remove polygon associations on parents
                            if (type === 'polygon') {
                                this.unAssociatePolygonFromAssociatedParents(features);
                            } else if (type === 'line') {
                                this.unAssociateLineFromAssociatedParents(features);
                            } else if (type === 'annotation') {
                                this.dissociateChildrenFeaturesFromAssociatedParents(features, reconciledMainObj);
                            }

                            // DCX-6774: update interest boundaries
                            if (type === 'lot') {
                                const {formerTenures, interests} = this.planModelService.getModel();
                                const nonSpatialInterests = this.interestReconciliationService.getNonSpatialInterests(formerTenures, interests);
                                this.interestService.updateInterestGeometry(nonSpatialInterests);
                            }
                        })
                    );
                })
            );
        });

        return observable;
    }

    private getPolygonsByParentFeature(parent: any) {
        if (parent.attributes.polygonids) {
            return this.spatialUtilService.getPolygonGraphics().filter((p: any) => parent.attributes.polygonids.includes(`[${p.attributes.id}]`));
        }
        return [];
    }

    private unAssociateParentFromAssociatedPolygons(parentFeatures: any[], parentType: string) {
        console.debug(`${this.title} - unAssociateParentFromAssociatedPolygons`);

        this.updateFeatures();

        parentFeatures.forEach((parentFeature: any) => {
            this.getPolygonsByParentFeature(parentFeature).forEach((polygon: any) => {
                if (polygon.attributes[`${parentType}id`]) {
                    polygon.attributes[`${parentType}id`] = null;
                    this.featuresToBeUpdated.polygon.push(polygon);
                    this.updateFeatures();
                } else if (polygon.attributes[`${parentType}ids`]) {
                    let parentIds = polygon.attributes[`${parentType}ids`].split(',');
                    if (parentIds.includes(`[${polygon.attributes.id}]`)) {
                        parentIds = parentIds.splice(parentIds.indexOf(`[${polygon.attributes.id}]`), 1);
                        polygon.attributes[`${parentType}ids`] = parentIds.join() || null;
                        this.featuresToBeUpdated.polygon.push(polygon);
                        this.updateFeatures();
                    }
                }
            });
        });
    }

    private unAssociatePolygonFromAssociatedParents(deletedPolygons: any[]) {
        console.debug(`${this.title} - unAssociatePolygonFromAssociatedParents`);

        const reconciledLots = this.getReconciledFeatures(this.spatialUtilService.getLots(), 'lot');
        const reconciledRoads = this.getReconciledFeatures(this.spatialUtilService.getRoads(), 'road');
        const reconciledInterests = this.getReconciledFeatures(this.spatialUtilService.getInterests(), 'interest');

        this.unAssociatePolygonFromAssociatedParent(deletedPolygons, reconciledLots, 'lot');
        this.unAssociatePolygonFromAssociatedParent(deletedPolygons, reconciledRoads, 'road');
        this.unAssociatePolygonFromAssociatedParent(deletedPolygons, reconciledInterests, 'interest');

        const polygonIdsToDelete = deletedPolygons.map(p => p.attributes.id);

        const reconciledRoadIds = reconciledRoads.map(r => r.attributes.id);
        const roadsLinkedToPolygons = polygonIdsToDelete.length
            ? this.spatialUtilService
                  .getRoads()
                  .filter(
                      (r: any) =>
                          this.spatialUtilService.parseIdsAttr(r.attributes.polygonids).some(id => polygonIdsToDelete.includes(id)) &&
                          !reconciledRoadIds.includes(r.attributes.id)
                  )
                  .map((r: any) => this.spatialUtilService.copyGraphic(r))
            : [];

        const roadFeatureMap = this.spatialUtilService.getFeatureMap(roadsLinkedToPolygons);

        deletedPolygons.forEach((feature: any) => {
            let parentId = undefined;

            ['lot', 'road', 'interest'].forEach(t => {
                let type = undefined;
                if (feature.attributes[t + 'id'] || feature.attributes[t + 'ids']) {
                    parentId = feature.attributes[t + 'id'] || feature.attributes[t + 'ids'];
                    type = t;
                }

                switch (type) {
                    case 'lot':
                        {
                            const lot = this.spatialUtilService.getLotGraphics().find((f: any) => f.attributes.id === parentId);
                            if (lot) {
                                this.featureAssociationService
                                    .updateLotAssociationChanges(lot, [feature], [])
                                    .pipe(
                                        tap(() =>
                                            console.debug(`${this.title} - unAssociatePolygonFromAssociatedParents - success for polygon:`, feature)
                                        )
                                    )
                                    .subscribe();
                            }
                        }
                        break;
                    case 'road':
                        {
                            const road = roadFeatureMap[parentId];
                            if (road) {
                                this.featureAssociationService
                                    .updateBusinessFeatureAssociationChanges(
                                        this.spatialUtilService.getRoadMapLayer(),
                                        road,
                                        [feature],
                                        [],
                                        'polygon'
                                    )
                                    .subscribe();
                            }
                        }
                        break;
                    case 'interest':
                        {
                            this.spatialUtilService.parseIdsAttr(parentId).forEach(id => {
                                const interest = this.getCachedInterest(id);
                                if (interest) {
                                    this.featureAssociationService
                                        .updateBusinessFeatureAssociationChanges(
                                            this.spatialUtilService.getInterestMapLayer(),
                                            interest,
                                            [feature],
                                            [],
                                            'polygon'
                                        )
                                        .pipe(tap(() => this.clearCachedInterest(id)))
                                        .subscribe();
                                }
                            });
                        }
                        break;
                }
            });
        });
    }

    private unAssociatePolygonFromAssociatedParent(deletedPolygons: any[], parentFeatures: any[], parentType: string) {
        console.debug(`${this.title} - unAssociatePolygonFromAssociatedParent`);

        this.updateFeatures();

        if (parentFeatures.length > 0) {
            parentFeatures.forEach((parentFeature: any) => {
                const polygons = this.getPolygonsByParentFeature(parentFeature);
                if (polygons.length !== deletedPolygons.length) {
                    this.featureAssociationService.resetPolygonIds(parentFeature, polygons);
                    this.featuresToBeUpdated[parentType].push(parentFeature);
                    this.updateFeatures();
                }
            });
        }
    }

    private unAssociateLineFromAssociatedParents(deletedLines: any[]) {
        console.debug(`${this.title} - unAssociateLinesFromAssociatedParents`);

        const reconciledInterests = this.getReconciledFeatures(this.spatialUtilService.getInterests(), 'interest').map(i =>
            this.getCachedInterest(i.attributes.id)
        );
        this.unAssociateLineFromAssociatedParent(deletedLines, reconciledInterests, 'interest');
    }

    private unAssociateLineFromAssociatedParent(deletedLines: any[], parentFeatures: any[], parentType: string) {
        console.debug(`${this.title} - unAssociateLineFromAssociatedParent`);

        this.updateFeatures();

        const polylineTypeLookups = this.getLineTypeLookups().filter(l => l.featureType === 'polyline');

        parentFeatures.forEach((parentFeature: any) => {
            let updateParentFeature = false;

            if (parentFeature.attributes.polylineids) {
                deletedLines.forEach((deletedLine: any) => {
                    parentFeature.attributes.polylineids.split(',').forEach((token: string) => {
                        if (token.replace(/\[/g, '').replace(/]/g, '') === String(deletedLine.attributes.id)) {
                            polylineTypeLookups.forEach(lookup => {
                                if (deletedLine.attributes.type === lookup.id) {
                                    updateParentFeature = true;
                                    const idArray = parentFeature.attributes.polylineids.split(',');
                                    const index = idArray.indexOf(`[${token}]`);
                                    idArray.splice(index, 1);
                                    parentFeature.attributes.polylineids = idArray.join(',');
                                }
                            });
                        }
                    });
                });
            }

            if (updateParentFeature) {
                this.featuresToBeUpdated[parentType].push(parentFeature);
                this.updateFeatures();
            }
        });
    }

    private dissociateChildrenFeaturesFromAssociatedParents(deletedAnnotations: any[], allDeletedFeatures: any) {
        console.debug(`${this.title} - dissociateChildrenFeaturesFromAssociatedParents`);

        const roadIds = deletedAnnotations.filter(a => a.attributes.featuretype === 'road').map(a => a.attributes.featureid);
        const roads = this.spatialUtilService
            .getRoads()
            .filter((r: any) => roadIds.includes(r.attributes.id))
            .map((r: any) => this.spatialUtilService.copyGraphic(r));
        const roadMap = this.spatialUtilService.getFeatureMap(roads);

        const interestIds = deletedAnnotations.filter(a => a.attributes.featuretype === 'interest').map(a => a.attributes.featureid);
        const interests = this.spatialUtilService
            .getInterests()
            .filter((i: any) => interestIds.includes(i.attributes.id))
            .map((i: any) => this.spatialUtilService.copyGraphic(i));
        const interestMap = this.spatialUtilService.getFeatureMap(interests);

        deletedAnnotations
            .filter(a => {
                const featureType = a.attributes.featuretype;
                const featureId = a.attributes.featureid;
                const childType = a.attributes.childtype;
                const childId = Number(a.attributes.childid);

                return (
                    featureType &&
                    featureId &&
                    childType &&
                    childId &&
                    !allDeletedFeatures[featureType].some((f: any) => f.attributes.id === featureId) &&
                    !allDeletedFeatures[childType].some((f: any) => f.attributes.id === childId)
                );
            })
            .forEach(a => {
                const featureType = a.attributes.featuretype;
                const featureId = a.attributes.featureid;
                const childType = a.attributes.childtype;
                const childId = Number(a.attributes.childid);

                if (featureType === 'lot') {
                    this.featureAssociationService
                        .saveLotDissociation(featureId, childId, childType)
                        .pipe(
                            tap(() => console.debug(`${this.title} - dissociateChildrenFeaturesFromAssociatedParents - success for annotation:`, a))
                        )
                        .subscribe();
                }

                if (featureType === 'interest') {
                    this.eventBus.cast('saveInterestDissociation', {interest: interestMap[featureId], childId: childId, childType: childType});
                }

                if (featureType === 'road' && a.attributes.featuresubtype === 11) {
                    this.roadService.unlinkAndUpdateRoads(roadMap[featureId], childId).subscribe();
                }
            });
    }

    private updateFeatures() {
        console.debug(`${this.title} - updateFeatures`);

        const featuresToBeUpdated = this.cloneFeatureMap(this.featuresToBeUpdated);
        this.featuresToBeUpdated = this.getCleanFeatureMap();
        const reconciledMainObj = this.getReconciledFeatureMap(featuresToBeUpdated);

        for (const [featureClass, features] of Object.entries(reconciledMainObj) as any) {
            if (features.length > 0) {
                this.spatialUtilService
                    .applyEdits(featureClass, null, features, null)
                    .pipe(
                        tap(res => {
                            console.debug(`${this.title} - updateFeatures - featureClass: ${featureClass} - result:`, res.updates);
                            this.spatialUtilService.updateMapGraphicGeometries(featureClass, features);
                        })
                    )
                    .subscribe();
            }
        }
    }

    private getReconciledFeatureMap(featureMap: FeatureMap): FeatureMap {
        console.debug(`${this.title} - getReconciledFeatureMap`);

        const output = {};

        for (const [featureClass, features] of Object.entries(featureMap) as any) {
            output[featureClass] = [];

            features.forEach((feature: any) => {
                if (!output[featureClass].find((f: any) => f.attributes.id === feature.attributes.id)) {
                    output[featureClass].push(feature);
                }
            });
        }

        return output;
    }

    private cloneFeatureMap(featureMap: FeatureMap): FeatureMap {
        console.debug(`${this.title} - cloneFeatureMap`);

        const output = {
            affectedLot: [],
            angle: [],
            annotation: [],
            controlPoint: [],
            point: [],
            line: [],
            polygon: [],
            lot: [],
            road: [],
            constructPoint: [],
            constructLine: [],
            interest: [],
            polyline: []
        };

        for (const [featureClass, features] of Object.entries(featureMap) as any) {
            output[featureClass].push(...features);
        }

        return output;
    }

    private getCleanFeatureMap(): FeatureMap {
        console.debug(`${this.title} - getCleanFeatureMap`);

        return {
            affectedLot: [],
            angle: [],
            annotation: [],
            controlPoint: [],
            point: [],
            line: [],
            polygon: [],
            lot: [],
            road: [],
            constructPoint: [],
            constructLine: [],
            interest: [],
            polyline: []
        };
    }
}
